// @ts-nocheck
import React from 'react';
import { ScraperStatus, SAVED_COLLECTIONS } from './types';

// Quick Collections
interface QuickCollectionsProps {
  isScraperConnected: boolean;
  hasCurrentCommand: boolean;
  onNavigate: (collection: typeof SAVED_COLLECTIONS[0]) => void;
}

export const QuickCollections: React.FC<QuickCollectionsProps> = ({ isScraperConnected, hasCurrentCommand, onNavigate }) => (
  <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 uppercase tracking-widest text-[10px] font-black text-gray-500">
      Quick Collections
    </div>
    <div className="p-4 space-y-3">
      {SAVED_COLLECTIONS.map((collection) => (
        <button
          key={collection.name}
          onClick={() => onNavigate(collection)}
          disabled={!isScraperConnected || hasCurrentCommand}
          className={`w-full bg-gradient-to-r ${collection.color} hover:opacity-90 disabled:from-gray-200 disabled:to-gray-300 disabled:text-gray-500 text-white py-3 px-4 rounded-lg text-sm font-bold transition-all shadow-md flex items-center justify-between`}
        >
          <span>{collection.name}</span>
          <span className="text-xs opacity-75 font-mono">{collection.shopId.slice(0, 8)}...</span>
        </button>
      ))}
      <p className="text-xs text-gray-400 text-center pt-2">
        Navigate to saved IG collections
      </p>
    </div>
  </div>
);

// Scraper Viewport
interface ScraperViewportProps {
  scraperStatus: ScraperStatus | null;
}

export const ScraperViewport: React.FC<ScraperViewportProps> = ({ scraperStatus }) => (
  <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
    <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 uppercase tracking-widest text-[10px] font-black text-gray-500">
      Scraper Viewport
    </div>
    <div className="p-6 space-y-4">
      <div className="flex flex-col">
        <span className="text-[10px] uppercase text-gray-400 font-bold mb-1">Current Page</span>
        <div className="bg-gray-50 border border-gray-200 p-3 rounded-lg text-xs break-all font-mono text-gray-600">
          {scraperStatus?.currentPageUrl || 'Not available'}
        </div>
      </div>
      <div className="flex flex-col">
        <span className="text-[10px] uppercase text-gray-400 font-bold mb-1">Detected Profile</span>
        <div className="text-base font-bold text-gray-900">
          @{scraperStatus?.currentUsername || 'Nobody'}
        </div>
      </div>
    </div>
  </div>
);

// Auto Mode Control
interface AutoModeProps {
  autoModeEnabled: boolean;
  includeSavedPosts: boolean;
  minDelay: number;
  maxDelay: number;
  isScraperConnected: boolean;
  hasCurrentCommand: boolean;
  onToggleAutoMode: () => void;
  onSetIncludeSavedPosts: (v: boolean) => void;
  onSetMinDelay: (v: number) => void;
  onSetMaxDelay: (v: number) => void;
  onUpdateSettings: () => void;
}

export const AutoModeControl: React.FC<AutoModeProps> = ({
  autoModeEnabled, includeSavedPosts, minDelay, maxDelay,
  isScraperConnected, hasCurrentCommand,
  onToggleAutoMode, onSetIncludeSavedPosts, onSetMinDelay, onSetMaxDelay, onUpdateSettings
}) => (
  <div className="bg-gradient-to-br from-blue-900 to-indigo-900 rounded-xl shadow-xl overflow-hidden text-white mb-6">
    <div className="px-6 py-4 bg-black/20 uppercase tracking-widest text-[10px] font-black text-blue-300">
      Auto Mode
    </div>
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg">Automatic Processing</h3>
          <p className="text-xs text-blue-200 opacity-80">Process tasks without manual intervention</p>
        </div>
        <button
          onClick={onToggleAutoMode}
          disabled={!isScraperConnected || hasCurrentCommand}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${autoModeEnabled ? 'bg-blue-400' : 'bg-gray-600'} disabled:opacity-50`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoModeEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      <div className="pt-2 border-t border-blue-800/50">
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-medium">Include Saved Posts</label>
            <button
              onClick={() => onSetIncludeSavedPosts(!includeSavedPosts)}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${includeSavedPosts ? 'bg-blue-400' : 'bg-gray-600'}`}
            >
              <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${includeSavedPosts ? 'translate-x-5' : 'translate-x-1'}`} />
            </button>
          </div>
          <p className="text-xs text-blue-200 opacity-60">Process saved collections when no tasks are pending</p>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-3">
          <div>
            <label className="text-xs font-medium block mb-1">Min Delay (seconds)</label>
            <input
              type="number"
              min="2"
              max="60"
              value={minDelay}
              onChange={(e) => onSetMinDelay(Math.max(2, parseInt(e.target.value) || 2))}
              className="w-full bg-blue-900/50 border border-blue-800/50 rounded px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Max Delay (seconds)</label>
            <input
              type="number"
              min="5"
              max="120"
              value={maxDelay}
              onChange={(e) => onSetMaxDelay(Math.max(minDelay + 1, parseInt(e.target.value) || 5))}
              className="w-full bg-blue-900/50 border border-blue-800/50 rounded px-3 py-1.5 text-sm"
            />
          </div>
        </div>

        <button
          onClick={onUpdateSettings}
          disabled={!isScraperConnected || hasCurrentCommand}
          className="w-full mt-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 disabled:text-gray-500 py-2 rounded text-sm font-bold transition-all"
        >
          Update Settings
        </button>
      </div>
    </div>
  </div>
);

// Manual Import Control
interface ManualControlProps {
  isScraperConnected: boolean;
  hasCurrentCommand: boolean;
  currentUsername: string | null | undefined;
  manualShopId: string;
  onSetManualShopId: (v: string) => void;
  onQuickImport: () => void;
  onManualImport: () => void;
}

export const ManualControl: React.FC<ManualControlProps> = ({
  isScraperConnected, hasCurrentCommand, currentUsername,
  manualShopId, onSetManualShopId, onQuickImport, onManualImport
}) => (
  <div className="bg-gray-900 rounded-xl shadow-xl overflow-hidden text-white">
    <div className="px-6 py-4 bg-gray-800 uppercase tracking-widest text-[10px] font-black text-gray-400">
      Manual Control
    </div>
    <div className="p-6 space-y-4">
      <button
        onClick={onQuickImport}
        disabled={!isScraperConnected || hasCurrentCommand || !currentUsername}
        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:from-gray-800 disabled:to-gray-800 disabled:text-gray-600 py-3 rounded-lg text-sm font-black transition-all shadow-lg"
      >
        QUICK IMPORT @{currentUsername || '...'}
      </button>
      <p className="text-xs opacity-50 text-center">
        Uses saved shop association or prompts for one
      </p>

      <div className="border-t border-gray-700 pt-4 mt-4">
        <p className="text-xs opacity-70 mb-3">
          Or specify a different shop ID:
        </p>
        <input
          type="text"
          placeholder="Target Shop ID"
          value={manualShopId}
          onChange={(e) => onSetManualShopId(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-white placeholder-gray-500"
        />
        <button
          onClick={onManualImport}
          disabled={!isScraperConnected || hasCurrentCommand || !manualShopId || !currentUsername}
          className="w-full mt-3 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-800 disabled:text-gray-600 py-3 rounded-lg text-sm font-black transition-all shadow-lg"
        >
          IMPORT TO SPECIFIC SHOP
        </button>
      </div>
    </div>
  </div>
);

// Offline Banner
export const OfflineBanner: React.FC = () => (
  <div className="bg-red-50 border border-red-200 rounded-xl p-6">
    <h3 className="text-sm font-bold text-red-800 uppercase mb-2">Service Offline</h3>
    <p className="text-xs text-red-700 leading-relaxed">
      The scraper background service is not communicating with this panel. Make sure you have the CLI running in the <code className="bg-red-100 px-1 rounded">sync:tasks</code> mode.
    </p>
  </div>
);
